import React, { useState } from 'react';
import theme from '@/data/theme';
import Modal from 'react-modal';
import { SeatAPIModel } from '@/apiModels/SeatReservation';
import { StyledFilledBorderButton } from '@/components/ui/Modals/EditTableModal/EditTableModal.styled.ts';
import {
  ModalButtonWrapper,
  StyledDisableButton,
  StyledDisabledIconWrapper,
  StyledModalContentWrapper,
  StyledTableNameHighlighter,
} from '@/components/ui/Modals/TableActionModal/TableActionsModal.style.ts';
import { toast } from 'react-toastify';
import useUpdateSeatStatus from '@/hooks/useUpateSeatStatus.ts';
import { useQueryClient } from '@tanstack/react-query';
import LoadingSplash from '@/components/ui/LoadingScreen';

interface ToggleTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  seatInformation: SeatAPIModel;
  eventId: string;
  action: 'enable' | 'disable';
}

const ToggleTableModal: React.FunctionComponent<ToggleTableModalProps> = ({
  onClose,
  isOpen,
  seatInformation,
  eventId,
  action,
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEnabling = action === 'enable';
  const actionText = isEnabling ? 'Enable' : 'Disable';
  const actionTextLower = actionText.toLowerCase();
  const actionTextPast = isEnabling ? 'enabled' : 'disabled';
  const actionTextPresent = isEnabling ? 'Enabling' : 'Disabling';

  const { mutate: updateSeatStatus } = useUpdateSeatStatus({
    cb: () => {},
    cbSuccess: () => {
      toast.success(`Table ${seatInformation.serial_no} has been ${actionTextPast} successfully`);
      queryClient.invalidateQueries({ queryKey: ['seat'] }).then(() => {});
      setIsSubmitting(false);
      onClose();
    },
    cbError: (error) => {
      toast.error(error?.message || `Failed to ${actionTextLower} table`);
    },
  });

  const handleModalClose = () => {
    onClose();
  };

  const handleToggleSeat = () => {
    setIsSubmitting(true);
    updateSeatStatus({
      payload: {
        is_enabled: isEnabling,
      },
      eventId: eventId,
      seatId: seatInformation.id,
    });
  };

  const renderIcon = () => {
    if (isEnabling) {
      return (
        <svg
          width="60"
          height="60"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="40" height="40" rx="20" fill="#f6f5fd" />
          <mask id="a" fill="#fff">
            <rect x="17.556" y="8" width="5.652" height="3.73" rx="1" />
          </mask>
          <rect
            x="17.556"
            y="8"
            width="5.652"
            height="3.73"
            rx="1"
            stroke="#865cd0"
            mask="url(#a)"
          />
          <path
            d="M18.778 13.464h3.112a3.25 3.25 0 0 1 3.25 3.25v4.768c-.84.214-1.36.777-1.603 1.391l-.004.005c-2.01-.416-4.283-.443-6.268-.009l-.013-.012a.2.2 0 0 1-.027-.04 2.48 2.48 0 0 0-1.697-1.346v-4.757a3.25 3.25 0 0 1 3.25-3.25Z"
            stroke="#865cd0"
          />
          <path
            d="M26.193 27.444H14.41a.5.5 0 0 1-.434-.252l-1.91-3.34a.35.35 0 0 1-.052-.249c.165-.747 1.78-.313 2.282.017.825.442 1.058.773 1.247 1.32a.54.54 0 0 0 .688.332c2.625-.914 5.991-.617 8.477.142a.504.504 0 0 0 .648-.38c.146-.76.313-1.375 1.304-1.993 1.115-.696 2.296-.116 1.943.695l-1.976 3.456a.5.5 0 0 1-.434.252ZM18.25 16.249c1.39.884.694 4.125.347 5.008m3.669-5.008c-1.391.884-.696 4.125-.348 5.008"
            stroke="#865cd0"
          />
          <path
            d="M21 26a4.501 4.501 0 0 0 8.657 1.722 4.501 4.501 0 0 0-8.314-3.444A4.5 4.5 0 0 0 21 26"
            fill="#f6f5fd"
            stroke="#865cd0"
          />
          <path d="m24 26 1 1 2-2" fill="#f6f5fd" />
          <path d="m24 26 1 1 2-2" stroke="#865cd0" />
        </svg>
      );
    }

    return (
      <svg
        width="60"
        height="60"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="40" height="40" rx="20" fill="#f6f5fd" />
        <mask id="a" fill="#fff">
          <rect x="17.556" y="8" width="5.652" height="3.73" rx="1" />
        </mask>
        <rect x="17.556" y="8" width="5.652" height="3.73" rx="1" stroke="#865cd0" mask="url(#a)" />
        <path
          d="M18.778 13.464h3.112a3.25 3.25 0 0 1 3.25 3.25v4.768c-.84.214-1.36.777-1.603 1.391l-.004.005c-2.01-.416-4.283-.443-6.268-.009l-.013-.012a.2.2 0 0 1-.027-.04 2.48 2.48 0 0 0-1.697-1.346v-4.757a3.25 3.25 0 0 1 3.25-3.25Z"
          stroke="#865cd0"
        />
        <path
          d="M26.193 27.444H14.41a.5.5 0 0 1-.434-.252l-1.91-3.34a.35.35 0 0 1-.052-.249c.165-.747 1.78-.313 2.282.017.825.442 1.058.773 1.247 1.32a.54.54 0 0 0 .688.332c2.625-.914 5.991-.617 8.477.142a.504.504 0 0 0 .648-.38c.146-.76.313-1.375 1.304-1.993 1.115-.696 2.296-.116 1.943.695l-1.976 3.456a.5.5 0 0 1-.434.252ZM18.25 16.249c1.39.884.694 4.125.347 5.008m3.669-5.008c-1.391.884-.696 4.125-.348 5.008"
          stroke="#865cd0"
        />
        <path d="M26 30.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z" fill="#f6f5fd" stroke="#865cd0" />
        <path d="m29 29-6-6z" fill="#f6f5fd" />
        <path d="m29 29-6-6" stroke="#865cd0" />
      </svg>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleModalClose}
      shouldCloseOnOverlayClick={false}
      style={{
        content: {
          maxWidth: '320px',
          minWidth: '296px',
          height: 'fit-content',
          borderRadius: '10px',
          inset: '0',
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          boxShadow: '0px 2px 6px 0px rgba(134, 92, 208, 0.50)',
          gap: '1rem',
        },
        overlay: {
          background: theme.colors.modalBackground,
        },
      }}
    >
      <LoadingSplash
        isVisible={isSubmitting}
        text={`${actionTextPresent} Table ${seatInformation.serial_no}...`}
        spinnerColor={theme.colors.primary}
        backgroundColor="rgba(255, 255, 255, 0.95)"
        textColor={theme.colors.primary}
        spinnerSize="40px"
        borderRadius="10px"
      />
      <StyledDisabledIconWrapper>{renderIcon()}</StyledDisabledIconWrapper>

      <StyledModalContentWrapper>
        Are you sure you want to {actionTextLower} {isEnabling ? '' : <br />} Table
        <StyledTableNameHighlighter> {seatInformation.serial_no}</StyledTableNameHighlighter>?
      </StyledModalContentWrapper>

      <ModalButtonWrapper>
        <StyledFilledBorderButton onClick={handleModalClose} disabled={isSubmitting}>
          Cancel
        </StyledFilledBorderButton>
        <StyledDisableButton onClick={handleToggleSeat} disabled={isSubmitting}>
          {isSubmitting ? `${actionTextPresent}...` : actionText}
        </StyledDisableButton>
      </ModalButtonWrapper>
    </Modal>
  );
};

export default ToggleTableModal;
